/**
 * Invitation Accept Page
 *
 * Allows invited users to accept team invitations and set up their accounts.
 * Route: /invite/:token
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { graphqlQuery, graphqlMutation } from '@business-assistant/shared/database/graphqlClient';

// =============================================================================
// Types
// =============================================================================

interface InvitationInfo {
  id: string;
  email: string;
  name: string | null;
  role: string;
  status: string;
  invitedByName: string | null;
  organizationName: string | null;
  message: string | null;
  expiresAt: string;
}

type AcceptStep = 'loading' | 'info' | 'setup' | 'processing' | 'success' | 'error';

// =============================================================================
// GraphQL
// =============================================================================

const INVITATION_INFO_QUERY = `
  query InvitationInfo($token: String!) {
    invitationInfo(token: $token) {
      id
      email
      name
      role
      status
      invitedByName
      organizationName
      message
      expiresAt
    }
  }
`;

const ACCEPT_INVITATION_MUTATION = `
  mutation AcceptInvitation($input: AcceptInvitationInput!) {
    acceptInvitation(input: $input) {
      success
      accessToken
      refreshToken
      expiresIn
      user {
        id
        email
        displayName
        role
        permissions {
          home
          progetti
          calendario
          hub
          contabilita
          impostazioni
        }
      }
      error
    }
  }
`;

// =============================================================================
// Component
// =============================================================================

export function InvitationAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [step, setStep] = useState<AcceptStep>('loading');
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    displayName: '',
    password: '',
    confirmPassword: '',
  });

  // If already logged in, show different message
  const isLoggedIn = !!currentUser;

  // Fetch invitation info on mount
  useEffect(() => {
    if (!token) {
      setError(t('auth.invalidInvitationLink', 'Invalid invitation link'));
      setStep('error');
      return;
    }

    const fetchInvitation = async () => {
      try {
        const response = await graphqlQuery<{ invitationInfo: InvitationInfo | null }>(
          INVITATION_INFO_QUERY,
          { token }
        );

        if (!response.invitationInfo) {
          setError(t('auth.invitationNotFound', 'Invitation not found or has expired'));
          setStep('error');
          return;
        }

        const inv = response.invitationInfo;
        setInvitation(inv);
        setFormData(prev => ({
          ...prev,
          displayName: inv.name || '',
        }));
        setStep('info');
      } catch (err) {
        console.error('[InvitationAccept] Error fetching invitation:', err);
        setError(err instanceof Error ? err.message : 'Failed to load invitation');
        setStep('error');
      }
    };

    fetchInvitation();
  }, [token, t]);

  // Google OAuth
  const googleLogin = useGoogleLogin({
    onSuccess: async (response) => {
      if (!token) return;

      setLoading(true);
      setStep('processing');

      try {
        const result = await graphqlMutation<{
          acceptInvitation: {
            success: boolean;
            accessToken?: string;
            refreshToken?: string;
            expiresIn?: number;
            user?: any;
            error?: string;
          };
        }>(ACCEPT_INVITATION_MUTATION, {
          input: {
            token,
            googleIdToken: response.access_token,
            displayName: formData.displayName || invitation?.name || undefined,
          },
        });

        if (!result.acceptInvitation.success) {
          throw new Error(result.acceptInvitation.error || 'Failed to accept invitation');
        }

        // Store tokens
        if (result.acceptInvitation.accessToken && result.acceptInvitation.refreshToken) {
          const { storeTokens } = await import('@business-assistant/shared/api/auth');
          storeTokens(
            result.acceptInvitation.accessToken,
            result.acceptInvitation.refreshToken,
            result.acceptInvitation.expiresIn || 900,
            result.acceptInvitation.user
          );
        }

        setStep('success');

        // Redirect after a short delay
        setTimeout(() => {
          navigate('/home');
          window.location.reload(); // Force reload to update auth state
        }, 2000);
      } catch (err) {
        console.error('[InvitationAccept] Google accept error:', err);
        setError(err instanceof Error ? err.message : 'Failed to accept invitation');
        setStep('setup');
        setLoading(false);
      }
    },
    onError: () => {
      setError(t('auth.googleLoginFailed', 'Google login failed. Please try again.'));
    },
    flow: 'implicit',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    // Validate
    if (!formData.password) {
      setError(t('auth.passwordRequired', 'Password is required'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsMismatch', 'Passwords do not match'));
      return;
    }

    if (formData.password.length < 8) {
      setError(t('auth.passwordTooShort', 'Password must be at least 8 characters'));
      return;
    }

    setLoading(true);
    setStep('processing');

    try {
      const result = await graphqlMutation<{
        acceptInvitation: {
          success: boolean;
          accessToken?: string;
          refreshToken?: string;
          expiresIn?: number;
          user?: any;
          error?: string;
        };
      }>(ACCEPT_INVITATION_MUTATION, {
        input: {
          token,
          password: formData.password,
          displayName: formData.displayName || invitation?.name || undefined,
        },
      });

      if (!result.acceptInvitation.success) {
        throw new Error(result.acceptInvitation.error || 'Failed to accept invitation');
      }

      // Store tokens
      if (result.acceptInvitation.accessToken && result.acceptInvitation.refreshToken) {
        const { storeTokens } = await import('@business-assistant/shared/api/auth');
        storeTokens(
          result.acceptInvitation.accessToken,
          result.acceptInvitation.refreshToken,
          result.acceptInvitation.expiresIn || 900,
          result.acceptInvitation.user
        );
      }

      setStep('success');

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/home');
        window.location.reload(); // Force reload to update auth state
      }, 2000);
    } catch (err) {
      console.error('[InvitationAccept] Password accept error:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
      setStep('setup');
      setLoading(false);
    }
  };

  const roleDisplay = (role: string) => {
    switch (role) {
      case 'admin': return t('roles.admin', 'Administrator');
      case 'team_member': return t('roles.teamMember', 'Team Member');
      case 'collaborator': return t('roles.collaborator', 'Collaborator');
      case 'guest': return t('roles.guest', 'Guest');
      default: return role;
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      backgroundColor: 'var(--bg-secondary, #f9fafb)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '480px',
        backgroundColor: 'var(--bg-primary, #ffffff)',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        padding: '2rem',
      }}>
        {/* Loading State */}
        {step === 'loading' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 1rem',
              border: '4px solid var(--border, #e5e7eb)',
              borderTopColor: 'var(--primary, #3b82f6)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ color: 'var(--text-muted, #6b7280)' }}>
              {t('auth.loadingInvitation', 'Loading invitation...')}
            </p>
          </div>
        )}

        {/* Error State */}
        {step === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              backgroundColor: 'var(--error-bg, #fef2f2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--error, #ef4444)" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h2 style={{ margin: '0 0 0.5rem', color: 'var(--text-primary, #111827)' }}>
              {t('auth.invitationError', 'Unable to Load Invitation')}
            </h2>
            <p style={{ margin: '0 0 1.5rem', color: 'var(--text-muted, #6b7280)' }}>
              {error}
            </p>
            <Link
              to="/login"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: 'var(--primary, #3b82f6)',
                color: '#ffffff',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: 500,
              }}
            >
              {t('auth.goToLogin', 'Go to Login')}
            </Link>
          </div>
        )}

        {/* Invitation Info */}
        {step === 'info' && invitation && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1rem',
                borderRadius: '50%',
                backgroundColor: 'var(--primary-bg, #eff6ff)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--primary, #3b82f6)" strokeWidth="2">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <h2 style={{ margin: '0 0 0.5rem', color: 'var(--text-primary, #111827)' }}>
                {t('auth.youveBeenInvited', "You've Been Invited!")}
              </h2>
              <p style={{ margin: 0, color: 'var(--text-muted, #6b7280)' }}>
                {invitation.invitedByName && invitation.organizationName ? (
                  <>
                    <strong>{invitation.invitedByName}</strong>
                    {' '}{t('auth.invitedYouToJoin', 'has invited you to join')}{' '}
                    <strong>{invitation.organizationName}</strong>
                  </>
                ) : (
                  t('auth.youveBeenInvitedToJoin', "You've been invited to join a team")
                )}
              </p>
            </div>

            {/* Invitation Details */}
            <div style={{
              padding: '1rem',
              backgroundColor: 'var(--bg-secondary, #f9fafb)',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)' }}>
                  {t('auth.invitedAs', 'Invited as')}
                </span>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 500 }}>
                  {roleDisplay(invitation.role)}
                </p>
              </div>
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)' }}>
                  {t('auth.email', 'Email')}
                </span>
                <p style={{ margin: '0.25rem 0 0', fontWeight: 500 }}>
                  {invitation.email}
                </p>
              </div>
            </div>

            {/* Personal message */}
            {invitation.message && (
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--bg-secondary, #f9fafb)',
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
                fontStyle: 'italic',
              }}>
                <p style={{ margin: 0, color: 'var(--text-primary, #111827)' }}>
                  "{invitation.message}"
                </p>
                {invitation.invitedByName && (
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: 'var(--text-muted, #6b7280)' }}>
                    - {invitation.invitedByName}
                  </p>
                )}
              </div>
            )}

            {isLoggedIn ? (
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--warning-bg, #fffbeb)',
                borderRadius: '0.5rem',
                marginBottom: '1rem',
              }}>
                <p style={{ margin: 0, color: 'var(--warning, #d97706)', fontSize: '0.875rem' }}>
                  {t('auth.alreadyLoggedIn', "You're currently logged in. Please log out first to accept this invitation with a new account.")}
                </p>
              </div>
            ) : (
              <button
                onClick={() => setStep('setup')}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  backgroundColor: 'var(--primary, #3b82f6)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                {t('auth.acceptInvitation', 'Accept Invitation')}
              </button>
            )}
          </div>
        )}

        {/* Account Setup */}
        {step === 'setup' && invitation && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: '0 0 0.5rem', color: 'var(--text-primary, #111827)' }}>
                {t('auth.setupAccount', 'Set Up Your Account')}
              </h2>
              <p style={{ margin: 0, color: 'var(--text-muted, #6b7280)' }}>
                {t('auth.chooseHowToSignIn', 'Choose how you want to sign in')}
              </p>
            </div>

            {error && (
              <div style={{
                padding: '0.75rem 1rem',
                marginBottom: '1rem',
                backgroundColor: 'var(--error-bg, #fef2f2)',
                color: 'var(--error, #ef4444)',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
              }}>
                {error}
              </div>
            )}

            {/* Google Option */}
            <button
              onClick={() => googleLogin()}
              disabled={loading}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                padding: '0.875rem',
                backgroundColor: '#ffffff',
                border: '2px solid var(--primary, #3b82f6)',
                borderRadius: '0.5rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                marginBottom: '1rem',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.connectGoogleAccount', 'Connect Google Account')}
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              margin: '1rem 0',
            }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border, #e5e7eb)' }} />
              <span style={{ color: 'var(--text-muted, #6b7280)', fontSize: '0.875rem' }}>
                {t('common.or', 'or')}
              </span>
              <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border, #e5e7eb)' }} />
            </div>

            {/* Password Form */}
            <form onSubmit={handlePasswordSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('auth.displayName', 'Display Name')}
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  placeholder={invitation.name || invitation.email.split('@')[0]}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('auth.password', 'Password')} *
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: 500 }}>
                  {t('auth.confirmPassword', 'Confirm Password')} *
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid var(--border, #e5e7eb)',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  backgroundColor: 'var(--primary, #3b82f6)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                {t('auth.setPassword', 'Set Password & Join')}
              </button>
            </form>

            <button
              onClick={() => setStep('info')}
              disabled={loading}
              style={{
                width: '100%',
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                color: 'var(--text-muted, #6b7280)',
              }}
            >
              {t('common.back', 'Back')}
            </button>
          </div>
        )}

        {/* Processing */}
        {step === 'processing' && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 1rem',
              border: '4px solid var(--border, #e5e7eb)',
              borderTopColor: 'var(--primary, #3b82f6)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ color: 'var(--text-muted, #6b7280)' }}>
              {t('auth.settingUpAccount', 'Setting up your account...')}
            </p>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 1.5rem',
              borderRadius: '50%',
              backgroundColor: 'var(--success-bg, #f0fdf4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--success, #22c55e)" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 style={{ margin: '0 0 0.5rem', color: 'var(--text-primary, #111827)' }}>
              {t('auth.welcomeAboard', 'Welcome Aboard!')}
            </h2>
            <p style={{ margin: 0, color: 'var(--text-muted, #6b7280)' }}>
              {t('auth.accountCreatedRedirecting', 'Your account has been created. Redirecting...')}
            </p>
          </div>
        )}

        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

export default InvitationAcceptPage;
